import { adRewardsAPI, ticketsAPI } from '../../services/api';

// Fallback if the settings API is unreachable — must match the
// ADSGRAM_BLOCK_ID default in app_settings (admin panel → Settings).
const DEFAULT_BLOCK_ID = '36005';

let cachedBlockId = null;

/**
 * AdsGram block id comes from the backend settings (app_settings.ADSGRAM_BLOCK_ID,
 * editable in the admin panel) via GET /v1/tickets/exchange-rate.
 * Cached for the session after the first successful fetch.
 */
export async function getAdsgramBlockId() {
  if (cachedBlockId) return cachedBlockId;
  try {
    const { adsgramBlockId } = await ticketsAPI.getExchangeRate();
    if (adsgramBlockId) cachedBlockId = String(adsgramBlockId);
  } catch (e) {
    console.warn('[AdsGram] failed to load block id from settings, using default:', e);
  }
  return cachedBlockId || DEFAULT_BLOCK_ID;
}

/**
 * AdsGram rejects show() with e.g. {error: "No available ad", code: 403} when
 * there is no ad inventory for this user right now. There is no pre-check API —
 * the only way to learn it is this rejection, so callers get an onNoAd callback
 * to fall back to their non-ad UI (docs.adsgram.ai/publisher/reward-interstitial-integration).
 */
function isNoAdError(err) {
  if (!err) return false;
  if (err.code === 403) return true;
  const text = typeof err === 'string' ? err : `${err.error ?? ''} ${err.description ?? ''}`;
  return /no\s+(available\s+)?ad/i.test(text);
}

/**
 * Full rewarded-ad flow, shared by Shop, LuckySpin and the Lair (HERO_REST / LAIR_FARM with targetId, v1.47.0):
 *   1. POST /v1/ad-rewards/initiate — register the pending reward
 *   2. show the AdsGram ad
 *   3. poll /v1/ad-rewards/{id}/status until the webhook grants it
 * Returns true if the reward was granted (or optimistically on poll timeout,
 * since the webhook may still arrive).
 *
 * onAdStart/onAdEnd bracket the actual ad playback — callers use them to
 * guard mount effects from re-firing while the ad overlay is open.
 * onNoAd fires when AdsGram has no ad to show (see isNoAdError above).
 */
export async function showAdAndGrant(rewardType, { shopItemId = null, targetId = null, onAdStart, onAdEnd, onNoAd } = {}) {
  if (!window.Adsgram) {
    console.error('[AdsGram] SDK not loaded (window.Adsgram is undefined)');
    return false;
  }
  let adEventId;
  try {
    const res = await adRewardsAPI.initiate(rewardType, shopItemId, targetId);
    adEventId = res.adEventId;
    console.log('[AdsGram] initiated, adEventId=', adEventId);
  } catch (e) {
    console.error('[AdsGram] initiate failed:', e);
    return false;
  }

  const blockId = await getAdsgramBlockId();
  onAdStart?.();
  console.log('[AdsGram] init blockId=', blockId);
  const AdController = window.Adsgram.init({ blockId });
  let result;
  try {
    result = await AdController.show();
    console.log('[AdsGram] show result:', result);
  } catch (err) {
    console.warn('[AdsGram] show error/cancelled:', err);
    onAdEnd?.();
    if (isNoAdError(err)) onNoAd?.(err);
    return false;
  }
  onAdEnd?.();
  if (!result?.done) {
    console.log('[AdsGram] not done, result.done=', result?.done);
    return false;
  }
  console.log('[AdsGram] ad completed, polling for grant status...');

  // Poll /v1/ad-rewards/{adEventId}/status until GRANTED/FAILED or timeout
  const POLL_INTERVAL_MS = 1500;
  const POLL_TIMEOUT_MS = 15000;
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    try {
      const { status } = await adRewardsAPI.getStatus(adEventId);
      console.log('[AdsGram] poll status:', status);
      if (status === 'GRANTED') return true;
      if (status === 'FAILED') return false;
      // PENDING — continue polling
    } catch (e) {
      console.warn('[AdsGram] poll error:', e);
    }
  }

  // Webhook may still arrive — resolve optimistically so UI refreshes
  console.warn('[AdsGram] polling timed out, resolving optimistically');
  return true;
}
